import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { PlusIcon } from '@heroicons/react/24/outline'
import { useLocation } from 'wouter'
import debounce from 'lodash.debounce'

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
  DialogOverlay,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { useCreateDocumentFormMutation } from '../hooks'
import { useListProjects } from '@/services/queries/projects'
import { useListDocumentPageThemeUserDefaults } from '@/services/queries/document_themes_user_defaults'
import { z } from 'zod'
import {
  ControllerRenderProps,
  SubmitHandler,
  useForm,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import {
  ChevronDownIcon,
  ChevronUpIcon,
  CheckIcon,
} from '@heroicons/react/24/outline'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
  PopoverPortal,
} from '@/components/ui/popover'
import { Spinner } from '@/components/ui/spinner'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'

import { Loading } from '@/components/ui/loading'
import { Input } from '@/components/ui/input'
import { cn } from '@/utils'
import { Files } from '@/components/ui/file'
import { SelectCADDropdown } from './SelectCADDropdown'
import { ThemeSelector } from './ThemeSelector'

export const CreateNewDocumentButton = () => {
  const [isOpen, setIsOpen] = useState(false)

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button
          onClick={() => setIsOpen(true)}
          className="rounded-full h-8 px-4 py-2 border border-white text-xs text-primary-40 bg-white flex items-center space-x-1 drop-shadow-none"
        >
          <PlusIcon className="h-4 w-4" />
          <span>New Document</span>
        </Button>
      </DialogTrigger>
      <DialogPortal>
        <DialogOverlay className="DialogOverlay">
          <DialogContent
            className="max-w-3xl pb-1 overflow-hidden min-h-[85vh] max-h-[540px] flex flex-col px-1"
            ariaLabel="Create new document dialog"
          >
            <DialogHeader>
              <div className="pb-5 border-b">
                <DialogTitle className="text-center">
                  Create New Document
                </DialogTitle>
              </div>
            </DialogHeader>
            <CreateDocumentForm />
          </DialogContent>
        </DialogOverlay>
      </DialogPortal>
    </Dialog>
  )
}

type NewDocumentForm = {
  documentName: string
  themeId: string
  cadId?: string
  files?: any[]
  projectId?: string
  projectName?: string
}

const formSchema = z.object({
  documentName: z
    .string({
      required_error: 'Document name is required.',
    })
    .min(2, {
      message: 'Document name must be at least 2 characters.',
    }),
  cadId: z.string().optional(),
  files: z.array(z.any()).optional(),
  projectId: z.string().optional(),
  projectName: z.string().optional(),
  themeId: z.string(),
})

const CreateDocumentForm = () => {
  const [, setLocation] = useLocation()
  const {
    mutate: createNewDocument,
    isPending: isCreatingNewDocument,
    isError,
    error,
  } = useCreateDocumentFormMutation({
    onSuccess: ({ projectId, documentId, cadVersionId }) => {
      const cadVersionParam = cadVersionId ? `cv=${cadVersionId}` : ''
      setLocation(
        `~/p/${projectId}/document/${documentId}/cad?${cadVersionParam}`,
      )
    },
  })

  const projectsList = useListProjects({ limit: 5 })
  const totalProjects = useMemo(
    () => projectsList.data?.pages[0].totalProjects || 0,
    [projectsList],
  )

  const { isLoading: isLoadingDefaultThemes, data: userDefaultThemes } =
    useListDocumentPageThemeUserDefaults()

  const defaultOperationTheme = userDefaultThemes?.find(
    (t) => t.theme_type === 'operation',
  )

  const form = useForm<NewDocumentForm>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      documentName: '',
      themeId: '',
      cadId: '',
      files: [],
      projectId: '',
      projectName: '',
    },
  })

  useEffect(() => {
    if (
      !isLoadingDefaultThemes &&
      defaultOperationTheme &&
      defaultOperationTheme.id
    ) {
      form.setValue('themeId', defaultOperationTheme.document_page_theme)
    }
  }, [defaultOperationTheme, form, isLoadingDefaultThemes])

  const formContainerRef = useRef<HTMLDivElement>(null)
  const subscribe = form.subscribe
  useEffect(() => {
    const callback = subscribe({
      formState: {
        values: true,
      },
      callback: (evt: any) => {
        if (
          formContainerRef &&
          formContainerRef.current &&
          evt.type === 'change' &&
          ['projectId', 'themeId'].includes(evt.name)
        ) {
          const containerEl = formContainerRef.current
          if (containerEl) {
            setTimeout(() => {
              containerEl.scrollTop = containerEl.scrollHeight
            }, 20)
          }
        }
      },
    })

    return () => callback()
  }, [subscribe, formContainerRef])

  const documentName = form.watch('documentName')
  const themeId = form.watch('themeId')
  const files = form.watch('files')
  const cadId = form.watch('cadId')
  const projectId = form.watch('projectId')
  const projectName = form.watch('projectName')

  const isSubmitButtonEnabled = useMemo(
    () =>
      !isCreatingNewDocument &&
      !!documentName &&
      !!themeId &&
      ((files?.length ?? 0) > 0 || !!cadId) &&
      (!!projectId || !!projectName),
    [
      isCreatingNewDocument,
      documentName,
      themeId,
      files?.length,
      cadId,
      projectId,
      projectName,
    ],
  )

  const validate = useCallback(
    (values: z.infer<typeof formSchema>) => {
      let isValid = true

      if (!values.documentName) {
        isValid = false
        form.setError('documentName', {
          type: 'required',
          message: 'Document name is required.',
        })
      }

      if (totalProjects <= 0) {
        if (!values.projectName) {
          isValid = false
          form.setError('projectName', {
            type: 'required',
            message: 'Project name is required.',
          })
        }

        if ((values.files || []).length < 1) {
          isValid = false
          form.setError('files', {
            type: 'required',
            message: 'Upload at least 1 CAD file.',
          })
        }
      }

      return { isValid }
    },
    [totalProjects, form],
  )

  const onSubmit: SubmitHandler<NewDocumentForm> = (values) => {
    try {
      const { isValid } = validate(values)
      if (!isValid) return
      const { documentName, cadId, files, projectId, projectName, themeId } =
        values
      createNewDocument({
        projectName,
        formValues: {
          documentName,
          cadId,
          files,
          projectId,
          themeId,
        },
        userThemeDefaultId: defaultOperationTheme?.id,
        isCreatingNewProject: !projectId && !!projectName,
        isUploadingNewCAD: (files?.length ?? 0) > 0 && !cadId,
      })
    } catch (e) {
      throw Error(`Unable to submit form: ${e}`)
    }
  }

  const onChangeFiles = useCallback((files) => {
    form.setValue('files', files)
    form.setValue('cadId', '')
  }, [])

  const isLoading = isLoadingDefaultThemes

  if (isLoading) {
    return <Spinner />
  }

  return (
    <Form {...form}>
      <form
        id="create-document-form"
        name="create-document-form"
        onSubmit={(event) => void form.handleSubmit(onSubmit)(event)}
        className="flex flex-col h-full overflow-y-auto"
      >
        <div
          ref={formContainerRef}
          className="flex-1 overflow-y-auto px-4 py-6"
        >
          <div className="mb-4">
            <FormField
              control={form.control}
              name="themeId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel htmlFor="themeId">Document Template</FormLabel>
                  <FormControl>
                    <ThemeSelector
                      selectedThemeId={themeId}
                      onSelect={(themeId) => {
                        field.onChange(themeId)
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <div className="mb-4">
            <FormField
              control={form.control}
              name="documentName"
              render={({ field }) =>
                form.watch('themeId') ? (
                  <FormItem>
                    <FormLabel htmlFor="document-name">Document Name</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        autoComplete="off"
                        id="document-name"
                        placeholder="Document name"
                        className="rounded-lg"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                ) : (
                  <></>
                )
              }
            />
          </div>
          <div className="mb-4">
            <FormField
              control={form.control}
              name="projectId"
              render={({ field }) =>
                form.watch('documentName') ||
                form.watch('projectId') ||
                form.watch('projectName') ? (
                  <FormItem>
                    <FormLabel htmlFor="document-name">
                      Project{' '}
                      <span className="text-slate-400">
                        | Assign document to a project
                      </span>
                    </FormLabel>
                    <FormControl>
                      <SearchableProjectSelect
                        field={field}
                        setValue={form.setValue}
                        formWatch={form.watch}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                ) : (
                  <></>
                )
              }
            />
          </div>
          <div className="mb-4 flex items-center">
            {form.watch('projectId') ||
            form.watch('projectName') ||
            (form.watch('files')?.length ?? 0) > 0 ||
            form.watch('cadId') ? (
              <>
                <div className="flex-1 space-x-2">
                  <FormField
                    control={form.control}
                    name="files"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel htmlFor="cad-id">
                          CAD{' '}
                          <span className="text-slate-400">
                            | Add CAD to document
                          </span>
                        </FormLabel>
                        <FormControl>
                          <Files
                            placeholder={
                              <span className="flex items-center text-slate-500">
                                + Upload new CAD{' '}
                                <span className="block ml-2 py-0.5 px-1 border-2 border-dashed">
                                  STEP file
                                </span>
                              </span>
                            }
                            testId="upload-cad"
                            isActive={(field.value?.length ?? 0) > 0}
                            onChange={onChangeFiles}
                            allowMultiple={false}
                            files={form.watch('files') ?? []}
                            validateFile={(file) => {
                              const validExtentions = ['step', 'stp']
                              const tok = file.name.toLowerCase().split('.')
                              const ext = tok[tok.length - 1]

                              const hasValidFileExtention =
                                validExtentions.includes(ext)
                              if (!hasValidFileExtention) {
                                form.setError('files', {
                                  type: 'extention',
                                  message: `Only files with the following extensions are allowed: ${validExtentions.join(
                                    ', ',
                                  )}.`,
                                })
                              }

                              const isValid = hasValidFileExtention

                              return isValid
                            }}
                            error={form.formState.errors.files?.message}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </div>
                <span className="mx-2 text-slate-400 text-sm mt-8">OR</span>
                <div className="flex-1 space-x-2 mt-8">
                  <FormField
                    control={form.control}
                    name="cadId"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <SelectCADDropdown
                            {...field}
                            projectId={form.watch('projectId')}
                            onCadSelect={() => {
                              form.setValue('files', [])
                            }}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </>
            ) : null}
          </div>

          {isError && (
            <Alert variant="destructive" data-testid="query-error-banner">
              <div className="flex items-center space-x-2">
                <AlertTitle className="mb-0">Error: </AlertTitle>
                <AlertDescription>
                  {error && error.message
                    ? error.message
                    : 'Failed to create a new document.'}
                </AlertDescription>
              </div>
            </Alert>
          )}
        </div>
        <div className="flex-none px-4 py-6 bg-white border-t flex justify-end">
          <Button type="submit" disabled={!isSubmitButtonEnabled}>
            {isCreatingNewDocument ? (
              <div className="flex items-center space-x-2">
                <span>Uploading</span>
                <Loading />
              </div>
            ) : (
              'Create Document'
            )}
          </Button>
        </div>
      </form>
    </Form>
  )
}

type SearchableProjectSelectProps = {
  field: ControllerRenderProps<NewDocumentForm, 'projectId'>
  setValue: UseFormSetValue<NewDocumentForm>
  formWatch: UseFormWatch<NewDocumentForm>
}
export const SearchableProjectSelect = ({
  field,
  setValue,
  formWatch,
}: SearchableProjectSelectProps) => {
  const [isFocused, setIsFocused] = useState(false)
  const [search, setSearch] = useState('')
  const [searchValue, setSearchValue] = useState('')
  const [open, setOpen] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  const projectsList = useListProjects({ limit: 5, search })
  const scrollRef = useRef<HTMLDivElement>(null)
  const projects = useMemo(
    () => projectsList.data?.pages.flatMap((page) => page.projects) || [],
    [projectsList],
  )
  const {
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetching: isFetchingProjects,
  } = projectsList

  const onScrollList = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    const threshold = 24
    const isNearBottom =
      el.scrollTop + el.clientHeight >= el.scrollHeight - threshold
    if (isNearBottom && hasNextPage && !isFetchingNextPage) {
      void fetchNextPage()
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage])

  const onCloseOptions = useCallback((event: MouseEvent) => {
    if (!containerRef.current?.contains(event.target as Node)) {
      setOpen(false)
    }
  }, [])

  const onEscapeOptions = useCallback((event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      setOpen(false)
    }
  }, [])

  const onCreateProject = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Enter') {
        event.preventDefault()
        setValue('projectName', searchValue)
        setValue('projectId', '')
        setOpen(false)
      }
    },
    [searchValue],
  )

  const ChevronIcon = useMemo(
    () => (open ? ChevronUpIcon : ChevronDownIcon),
    [open],
  )

  const showNote = useMemo(
    () => !!searchValue && isFocused && open,
    [searchValue, isFocused, open],
  )

  const onUpdateSearch = useCallback(
    debounce((e) => {
      setSearch(e.target.value)
    }, 500),
    [],
  )

  useEffect(() => {
    if (open) {
      setTimeout(() => {
        inputRef.current?.focus()
      }, 100)
      document.addEventListener('click', onCloseOptions)
      document.addEventListener('keydown', onEscapeOptions)
    } else {
      document.removeEventListener('click', onCloseOptions)
      document.removeEventListener('keydown', onEscapeOptions)
    }

    return () => {
      document.removeEventListener('click', onCloseOptions)
      document.removeEventListener('keydown', onEscapeOptions)
    }
  }, [open, onCloseOptions, onEscapeOptions])

  useEffect(() => {
    if (showNote) {
      document.addEventListener('keydown', onCreateProject)
    } else {
      document.removeEventListener('keydown', onCreateProject)
    }

    return () => {
      document.removeEventListener('keydown', onCreateProject)
    }
  }, [showNote, onCreateProject])

  return (
    <Popover open={open}>
      <PopoverTrigger asChild>
        <div ref={containerRef} className="relative w-full flex items-center">
          {!!formWatch('projectName') && (
            <span className="absolute left-2 text-xs py-0.5 px-1.5 bg-blue-100 text-blue-500 rounded">
              NEW
            </span>
          )}
          <Input
            {...field}
            data-testid="project-search"
            ref={inputRef}
            type="text"
            placeholder="Type to find existing project or create a new one"
            value={searchValue}
            autoComplete="off"
            onChange={(e) => {
              onUpdateSearch(e)
              setSearchValue(e.target.value)
            }}
            onFocus={() => {
              setOpen(true)
              setIsFocused(true)
            }}
            onBlur={() => setIsFocused(false)}
            className={cn('w-full p-2 border rounded-lg focus:outline-none', {
              'pr-[12rem]': showNote,
              'pl-[3.5rem]': !!formWatch('projectName'),
            })}
          />
          <ChevronIcon className="absolute right-2 top-2.5 w-4 h-4 text-gray-500 pointer-events-none" />
          {showNote && (
            <span className="absolute right-8 top-2.5 text-xs text-blue-500">
              Hit [enter] to create new
            </span>
          )}
        </div>
      </PopoverTrigger>

      <PopoverPortal>
        <PopoverContent
          className="bg-white z-[1000] border rounded-lg shadow-lg"
          sideOffset={4}
          align="start"
          style={{
            width: containerRef.current?.offsetWidth,
            height: 'auto',
            maxHeight: '200px',
          }}
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <div
            ref={scrollRef}
            className="max-h-[180px] overflow-y-auto overscroll-contain"
            onScroll={onScrollList}
            onWheelCapture={(e) => e.stopPropagation()}
            onTouchMoveCapture={(e) => e.stopPropagation()}
            style={{ WebkitOverflowScrolling: 'touch' }}
          >
            {projects.length > 0 ? (
              projects.map((project, i) => (
                <div
                  data-testid={`project-option--${i}`}
                  key={project.id}
                  className={`flex items-center justify-between p-2 rounded-md cursor-pointer hover:bg-gray-100 ${
                    formWatch('projectId') === project.id ? 'bg-gray-200' : ''
                  }`}
                  onClick={() => {
                    setValue('projectId', project.id as string)
                    setValue('projectName', '')
                    setSearchValue(project.name)
                    setSearch(project.name)
                    setOpen(false)
                    inputRef.current?.blur()
                  }}
                >
                  {project.name}
                  {formWatch('projectId') === project.id && (
                    <CheckIcon className="w-4 h-4" />
                  )}
                </div>
              ))
            ) : !(isFetchingNextPage || isFetchingProjects) ? (
              <p className="p-1 text-gray-500">No results found</p>
            ) : null}
            {(isFetchingNextPage || isFetchingProjects) && (
              <div className="p-2 flex items-center justify-center">
                <Spinner />
              </div>
            )}
          </div>
        </PopoverContent>
      </PopoverPortal>
    </Popover>
  )
}
